<script setup lang="ts">
/**
 * The list around `NxListRow`s: a `ul` without bullets, the global list font
 * reset undone, rows stacked with a small gap (or divided by hairlines).
 * Rows inside render as `li` on their own.
 */
import { provide } from 'vue';
import { listRowTagKey } from './internal';

withDefaults(
  defineProps<{
    /** Hairlines between rows instead of a gap. */
    divided?: boolean;
    /**
     * Caps the list's height at a CSS length (a token like
     * `var(--nxt-area-size-2xl)` or `50vh`); longer content scrolls in the
     * list itself and never scrolls the page behind it. Unset: the list is as
     * tall as its rows.
     */
    maxHeight?: string;
  }>(),
  { divided: false, maxHeight: undefined }
);

provide(listRowTagKey, 'li');
</script>

<template>
  <ul
    class="nx-list"
    :class="{ 'is-divided': divided, 'has-max-height': !!maxHeight }"
    :style="maxHeight ? { '--list-max-height': maxHeight } : undefined"
    role="list"
  >
    <slot />
  </ul>
</template>

<style scoped>
.nx-list {
  display: flex;
  flex-direction: column;
  /* Rows end their lines in an ellipsis, so the list never needs its text's
     width: without this a list in a grid or flex track took the nowrap
     text's min-content and pushed the row actions out of a narrow panel. */
  min-width: 0;
  gap: var(--nxt-space-2xs);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
}
.nx-list.is-divided {
  gap: 0;
}
/* Opt-in height cap: the list scrolls in place. */
.nx-list.has-max-height {
  max-height: var(--list-max-height);
  overflow-y: auto;
  overscroll-behavior: contain;
}
/* Divided rows are one block: square, a hairline between them, and hover and
   selection only tint the row — a frame of their own drew a square box with
   the grey divider as its bottom line, and a rounded one on the last row
   (I-3178). */
.nx-list.is-divided > :deep(.nx-list-row) {
  border-radius: 0;
}
.nx-list.is-divided > :deep(.nx-list-row:not(:last-child)) {
  border-bottom-color: var(--nxt-border-subtle);
}
.nx-list.is-divided > :deep(.nx-list-row:is(.is-selected, .is-interactive:hover)) {
  border-top-color: transparent;
  border-inline-color: transparent;
}
.nx-list.is-divided > :deep(.nx-list-row:last-child:is(.is-selected, .is-interactive:hover)) {
  border-bottom-color: transparent;
}
</style>
